import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { of, Subject } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import {
  BatchEventTypeTargetInterface,
  DateRangeType,
  durationStringToMinutes,
  EventTypeDateRangeInterface,
  GuestService,
  RelativeTimeUnit,
  ServiceEventType,
  TeamEventMeetingType,
} from '@vendasta/meetings';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { RemoveServiceDialogComponent } from '../scheduling-page/remove-service-dialog/remove-service-dialog.component';
import {
  isTimeSlotLaterThanNow,
  TimezoneOption,
} from '../bookme-calendar/bookme-calendar.component';
import { TimeSlot } from '../bookme/bookme';
import { CartItem } from '../shared/booking-cart/booking-cart.component';
import { ANY_AVAILABLE_PROVIDER, getTimezoneNames, getWeekdayHeaders, getZoneFromTimezone, trimTimeRangeToFromNow } from '../util';
import { resolveBookmeLocale } from '../localize-bootstrap.service';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

function buildTimezoneOptionDisplay(timezone: string): string {
  const now = new Date();
  return `${getZoneFromTimezone(timezone, 0, now)} (${timezone}) : ${formatInTimeZone(now, timezone, 'HH:mm')}`;
}

export interface DayCell {
  day: number;
  disabled: boolean;
  selected: boolean;
  today: boolean;
  date: Date | null;
}

@Component({
  selector: 'app-datetime-selection-page',
  standalone: false,
  templateUrl: './datetime-selection-page.component.html',
  styleUrls: ['./datetime-selection-page.component.scss'],
})
export class DatetimeSelectionPageComponent implements OnInit {
  private static readonly EMPTY_CELL: DayCell = {
    day: 0,
    disabled: true,
    selected: false,
    today: false,
    date: null,
  };
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly storeService = inject(BookMeNowStoreService);
  private readonly guestService = inject(GuestService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('tzSearchInput') tzSearchInput: ElementRef<HTMLInputElement>;

  cartItems = signal<CartItem[]>([]);
  isRescheduleMode = signal(false);
  isSingleEventRoute = signal(false);
  canGoBack = signal(true);

  // Calendar state
  calendarExpanded = signal(true);
  currentMonth = signal<Date>(new Date());
  selectedDate = signal<Date | null>(null);

  // Store-driven state
  timezone = signal<string>('');
  allTimeSlots = signal<TimeSlot[]>([]);
  isLoading = signal<boolean>(false);
  loadError = signal<boolean>(false);
  dateRange = signal<EventTypeDateRangeInterface | null>(null);
  private slotsLoaded = signal<boolean>(false);
  private pendingTimeSlotRestore: TimeSlot | null = null;
  initialLoadComplete = signal(false);

  /** Cached targets for batch time slot loading (month nav, tz change) */
  private targets: BatchEventTypeTargetInterface[] = [];

  // Selection state
  selectedSlotIndex = signal<number | null>(null);

  // Time filters
  readonly timeFilters = ['All', 'Morning', 'Afternoon', 'Evening'] as const;
  readonly timeFilterLabels: Record<string, string> = {
    All: 'CALENDAR.FILTER_ALL',
    Morning: 'CALENDAR.FILTER_MORNING',
    Afternoon: 'CALENDAR.FILTER_AFTERNOON',
    Evening: 'CALENDAR.FILTER_EVENING',
  };
  activeFilter = signal('All');
  readonly weekdayHeaders = getWeekdayHeaders();

  // Timezone dropdown
  showTimezoneDropdown = signal(false);
  timezoneSearch = signal('');
  private allTimezoneOptions: TimezoneOption[] = [];
  private readonly cancelPendingLoad$ = new Subject<void>();

  // --- Computed signals ---

  monthTitle = computed(() => {
    const d = this.currentMonth();
    return d.toLocaleDateString(resolveBookmeLocale(), { month: 'long', year: 'numeric' });
  });

  timezoneDisplay = computed(() => {
    const tz = this.timezone();
    if (!tz) return '';
    return buildTimezoneOptionDisplay(tz);
  });

  filteredTimezones = computed(() => {
    const search = this.timezoneSearch().toLowerCase().trim();
    if (!search) return this.allTimezoneOptions;
    return this.allTimezoneOptions.filter(
      (o) =>
        o.display.toLowerCase().includes(search) ||
        o.timezone.toLowerCase().includes(search)
    );
  });

  private effectiveMinDate = computed<Date>(() => {
    const dr = this.dateRange();
    if (
      dr?.dateRangeType === DateRangeType.CUSTOM &&
      dr.customDateRange?.start
    ) {
      const s = dr.customDateRange.start;
      return new Date(s.getFullYear(), s.getMonth(), s.getDate());
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  private effectiveMaxDate = computed<Date | null>(() => {
    const dr = this.dateRange();
    return dr ? this.deriveMaxDateFromRange(dr) : null;
  });

  canGoPrev = computed(() => {
    const current = this.currentMonth();
    const minDate = this.effectiveMinDate();
    return (
      current.getFullYear() > minDate.getFullYear() ||
      (current.getFullYear() === minDate.getFullYear() &&
        current.getMonth() > minDate.getMonth())
    );
  });

  canGoNext = computed(() => {
    const current = this.currentMonth();
    const maxDate = this.effectiveMaxDate();
    if (!maxDate) return true;
    return (
      current.getFullYear() < maxDate.getFullYear() ||
      (current.getFullYear() === maxDate.getFullYear() &&
        current.getMonth() < maxDate.getMonth())
    );
  });

  monthWeeks = computed(() => {
    const month = this.currentMonth();
    const year = month.getFullYear();
    const m = month.getMonth();
    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const selected = this.selectedDate();
    const slots = this.allTimeSlots();
    const tz = this.timezone() || 'UTC';
    const minDate = this.effectiveMinDate();
    const maxDate = this.effectiveMaxDate();
    const hasSlotsData = this.slotsLoaded();

    const firstDay = new Date(year, m, 1).getDay();
    const daysInMonth = new Date(year, m + 1, 0).getDate();
    const todayKey = format(todayStart, 'yyyy-MM-dd');
    const selectedKey = selected ? format(selected, 'yyyy-MM-dd') : null;

    let slotDateSet: Set<string> | null = null;
    if (hasSlotsData && Array.isArray(slots) && slots.length > 0) {
      slotDateSet = new Set(
        slots.map((s) => formatInTimeZone(s.start, tz, 'yyyy-MM-dd'))
      );
    }

    const weeks: DayCell[][] = [];
    let currentWeek: DayCell[] = [];

    for (let i = 0; i < firstDay; i++) {
      currentWeek.push(DatetimeSelectionPageComponent.EMPTY_CELL);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate = new Date(year, m, d);
      const isPast = cellDate < todayStart;
      const outOfRange =
        cellDate < minDate || (maxDate !== null && cellDate > maxDate);
      const cellKey = format(cellDate, 'yyyy-MM-dd');

      let noSlots = false;
      if (hasSlotsData) {
        noSlots = !slotDateSet || !slotDateSet.has(cellKey);
      }

      currentWeek.push({
        day: d,
        disabled: isPast || outOfRange || noSlots,
        selected: selectedKey === cellKey,
        today: todayKey === cellKey,
        date: cellDate,
      });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(DatetimeSelectionPageComponent.EMPTY_CELL);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  });

  selectedWeekIndex = computed(() => {
    const idx = this.monthWeeks().findIndex((week) =>
      week.some((d) => d.selected)
    );
    return Math.max(idx, 0);
  });

  timeSlotsForSelectedDate = computed(() => {
    const selected = this.selectedDate();
    const slots = this.allTimeSlots();
    const tz = this.timezone() || 'UTC';
    if (!selected || !Array.isArray(slots)) return [];

    const selectedKey = format(selected, 'yyyy-MM-dd');
    return slots.filter(
      (slot) =>
        isTimeSlotLaterThanNow(slot.start) &&
        formatInTimeZone(slot.start, tz, 'yyyy-MM-dd') === selectedKey
    );
  });

  displayTimeSlots = computed(() => {
    const slots = this.timeSlotsForSelectedDate();
    const filter = this.activeFilter();
    const tz = this.timezone() || 'UTC';

    return slots
      .map((slot) => {
        const label = slot.start.toLocaleTimeString(resolveBookmeLocale(), {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: tz,
        });
        const hour = Number(formatInTimeZone(slot.start, tz, 'H'));
        return { label, hour, slot };
      })
      .filter((item) => {
        if (filter === 'All') return true;
        switch (filter) {
          case 'Morning':
            return item.hour < 12;
          case 'Afternoon':
            return item.hour >= 12 && item.hour < 17;
          case 'Evening':
            return item.hour >= 17;
          default:
            return true;
        }
      })
      .sort((a, b) => new Date(a.slot.start).getTime() - new Date(b.slot.start).getTime());
  });

  currentView = computed<
    'error' | 'no-available-dates' | 'select-a-date' | 'select-a-time'
  >(() => {
    if (this.loadError()) return 'error';

    const slots = this.allTimeSlots();
    if (this.selectedDate()) return 'select-a-time';
    if (this.slotsLoaded()) {
      if (Array.isArray(slots) && slots.length > 0) return 'select-a-date';
      return 'no-available-dates';
    }
    return 'select-a-date';
  });

  noSlotsForMonth = computed(() => {
    if (!this.slotsLoaded()) return false;
    const slots = this.allTimeSlots();
    return !Array.isArray(slots) || slots.length === 0;
  });

  isCtaDisabled = computed(() => this.selectedSlotIndex() === null);

  // --- Lifecycle ---

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    this.isSingleEventRoute.set(
      !!params['calendarId'] && !!params['meetingTypeId']
    );
    this.buildTimezoneOptions();
    this.subscribeToStore();
    this.restoreSelectionFromStore();
    this.autoSelectToday();

    this.isRescheduleMode.set(this.storeService.getIsRescheduleMode());
    this.canGoBack.set(
      !this.isRescheduleMode() &&
      !(this.isSingleEventRoute() && !this.storeService.getShouldShowProviderStep())
    );

    if (this.isRescheduleMode()) {
      this.initRescheduleMode();
    } else {
      this.subscribeToSelectedEvents();
    }
  }

  private restoreSelectionFromStore(): void {
    const state = this.storeService.getCurrentState();

    if (state?.selectedDate) {
      const date = new Date(state.selectedDate);
      this.selectedDate.set(date);
      this.currentMonth.set(new Date(date.getFullYear(), date.getMonth(), 1));
    }

    if (state?.selectedTimeSlot) {
      this.pendingTimeSlotRestore = state.selectedTimeSlot;
      // Re-set in store to ensure timeSlot$ emits for confirm page
      this.storeService.setSelectedTimeSlot(state.selectedTimeSlot);
    }
  }

  private autoSelectToday(): void {
    if (!this.selectedDate()) {
      const today = new Date();
      this.selectedDate.set(today);
      this.currentMonth.set(new Date(today.getFullYear(), today.getMonth(), 1));
      this.storeService.setSelectedDate(today);
    }
  }

  private initRescheduleMode(): void {
    // Populate cart from V2 data (set by BookingLayoutComponent)
    this.storeService.guestBookedMeetingV2$
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((meetingInfo) => {
        // meetings[] is always populated (1 for single, N for batch)
        this.cartItems.set(
          (meetingInfo.meetings || []).map((m) => ({
            name: m.title || '',
            duration: this.durationMinutes(m.startTime, m.endTime),
            providerName: m.hostUserNames?.join(', ') || '',
            initials: m.title?.charAt(0)?.toUpperCase() || '',
          }))
        );
      });

    // Wait for reschedule targets (set by BookingLayoutComponent from V2 meetings[])
    this.storeService.rescheduleTargets$
      .pipe(
        filter((t) => t.length > 0),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((rescheduleTargets) => {
        this.targets = rescheduleTargets;
        this.loadTimeSlots();
      });
  }

  private durationMinutes(start: Date, end: Date): number {
    if (!start || !end) return 0;
    return Math.round(
      (new Date(end).getTime() - new Date(start).getTime()) / 60000
    );
  }

  private subscribeToSelectedEvents(): void {
    this.storeService.selectedBookingEvents$
      .pipe(
        filter((events) => events.length > 0),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((events) => {
        const providerMap = new Map<string, string>();
        const items = events.map((event) => {
          const providerName = this.storeService.getProviderNameForEvent(event);
          if (providerName !== ANY_AVAILABLE_PROVIDER) {
            providerMap.set(event.id, providerName);
          }
          return {
            name: event.name,
            providerName,
            duration: event.duration
              ? durationStringToMinutes({ duration: event.duration })
              : 0,
            initials:
              event.name
                ?.split(' ')
                .map((w) => w.charAt(0))
                .join('')
                .toUpperCase()
                .substring(0, 2) || '',
          };
        });
        if (providerMap.size > 0) {
          const existing = this.storeService.getEventProviderMapRaw();
          providerMap.forEach((v, k) => existing.set(k, v));
          this.storeService.setEventProviderMap(existing);
        }
        this.cartItems.set(items);

        this.applyDateRangeFromSelectedEvents(events);
        this.loadTimeSlots();
      });
  }

  private dateRangeLockedByEvents = false;

  private applyDateRangeFromSelectedEvents(events: ServiceEventType[]): void {
    const state = this.storeService.getCurrentState();
    const meetingTypes = state.meetingTypes || [];
    const singleMeetingType = state.meetingType;

    const ranges: EventTypeDateRangeInterface[] = [];
    for (const event of events) {
      const fromList = meetingTypes.find(
        (mt) => mt?.id === event.id
      )?.dateRange;
      if (fromList) {
        ranges.push(fromList);
        continue;
      }
      if (singleMeetingType?.id === event.id && singleMeetingType.dateRange) {
        ranges.push(singleMeetingType.dateRange);
      }
    }
    if (!ranges.length) {
      return;
    }
    const maxDates = ranges
      .map((dr) => this.deriveMaxDateFromRange(dr))
      .filter((d): d is Date => !!d);
    if (!maxDates.length) {
      return;
    }
    const minMax = new Date(Math.min(...maxDates.map((d) => d.getTime())));
    const today = new Date();
    this.dateRange.set({
      dateRangeType: DateRangeType.CUSTOM,
      customDateRange: {
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        end: minMax,
      },
    });
    this.dateRangeLockedByEvents = true;
  }

  private deriveMaxDateFromRange(dr: EventTypeDateRangeInterface): Date | null {
    if (!dr) return null;
    if (dr.dateRangeType === DateRangeType.CUSTOM && dr.customDateRange?.end) {
      return dr.customDateRange.end;
    }
    if (
      dr.dateRangeType === DateRangeType.RELATIVE &&
      dr.relativeDateRange?.value
    ) {
      const now = new Date();
      const val = dr.relativeDateRange.value;
      switch (dr.relativeDateRange.unit) {
        case RelativeTimeUnit.DAYS:
          return new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + val
          );
        case RelativeTimeUnit.WEEKS:
          return new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + val * 7
          );
        case RelativeTimeUnit.MONTHS:
        default:
          return this.addMonthsClamped(now, val);
      }
    }
    return null;
  }

  /** Clamp the day to the target month's last so Jan 31 + 1mo stays in Feb. */
  private addMonthsClamped(base: Date, months: number): Date {
    const targetMonth = base.getMonth() + months;
    // new Date(y, m+1, 0) → last day of month m.
    const lastDayOfTarget = new Date(
      base.getFullYear(),
      targetMonth + 1,
      0
    ).getDate();
    return new Date(
      base.getFullYear(),
      targetMonth,
      Math.min(base.getDate(), lastDayOfTarget)
    );
  }

  // --- Timezone dropdown ---

  private buildTimezoneOptions(): void {
    this.allTimezoneOptions = getTimezoneNames().map((tz) => {
      return {
        timezone: tz,
        display: buildTimezoneOptionDisplay(tz),
      };
    });
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showTimezoneDropdown()) {
      this.closeTimezoneDropdown();
    }
  }

  openTimezoneDropdown(): void {
    this.showTimezoneDropdown.set(true);
    this.timezoneSearch.set('');
    setTimeout(() => this.tzSearchInput?.nativeElement.focus(), 0);
  }

  closeTimezoneDropdown(): void {
    this.showTimezoneDropdown.set(false);
    this.timezoneSearch.set('');
  }

  onTimezoneSearchInput(event: Event): void {
    this.timezoneSearch.set((event.target as HTMLInputElement).value);
  }

  selectTimezone(tz: string): void {
    this.timezone.set(tz);
    this.storeService.setTimezone(tz, false);
    this.showTimezoneDropdown.set(false);
    this.timezoneSearch.set('');
    this.selectedSlotIndex.set(null);
    this.activeFilter.set('All');
    this.loadTimeSlots();
  }

  // --- Store subscriptions ---

  private subscribeToStore(): void {
    this.storeService.timezone$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tz) => this.timezone.set(tz));

    this.storeService.dateRange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dr) => {
        if (this.dateRangeLockedByEvents) {
          return;
        }
        this.dateRange.set(dr);
        if (
          dr?.dateRangeType === DateRangeType.CUSTOM &&
          dr.customDateRange?.start
        ) {
          const start = dr.customDateRange.start;
          this.currentMonth.set(
            new Date(start.getFullYear(), start.getMonth(), 1)
          );
        }
      });
  }

  // --- Calendar actions ---

  onPrevMonth(): void {
    if (!this.canGoPrev()) return;
    const current = this.currentMonth();
    this.currentMonth.set(
      new Date(current.getFullYear(), current.getMonth() - 1, 1)
    );
    this.selectedDate.set(null);
    this.selectedSlotIndex.set(null);
    this.activeFilter.set('All');
    this.slotsLoaded.set(false);
    this.loadTimeSlots();
  }

  onNextMonth(): void {
    if (!this.canGoNext()) return;
    const current = this.currentMonth();
    this.currentMonth.set(
      new Date(current.getFullYear(), current.getMonth() + 1, 1)
    );
    this.selectedDate.set(null);
    this.selectedSlotIndex.set(null);
    this.activeFilter.set('All');
    this.slotsLoaded.set(false);
    this.loadTimeSlots();
  }

  onDateSelect(dayCell: DayCell): void {
    if (dayCell.disabled || dayCell.day === 0 || !dayCell.date) return;
    this.selectedDate.set(dayCell.date);
    this.selectedSlotIndex.set(null);
    this.activeFilter.set('All');
    this.storeService.setSelectedDate(dayCell.date);
  }

  onTimeSlotSelect(index: number, slot: TimeSlot): void {
    this.selectedSlotIndex.set(index);

    if (this.isRescheduleMode()) {
      const originalSlot = this.storeService.getOriginalMeetingTimeSlot();
      if (originalSlot) {
        const duration =
          new Date(originalSlot.end).getTime() -
          new Date(originalSlot.start).getTime();
        this.storeService.setSelectedTimeSlot({
          ...slot,
          end: new Date(slot.start.getTime() + duration),
        });
        return;
      }
    }

    this.storeService.setSelectedTimeSlot(slot);
  }

  onFilterChange(filter: string): void {
    this.activeFilter.set(filter);
    this.selectedSlotIndex.set(null);
  }

  toggleCalendarExpand(): void {
    this.calendarExpanded.update((v) => !v);
  }

  onRemoveCartItem(index: number): void {
    const events = this.storeService.getSelectedBookingEvents();
    const event = events[index];
    if (!event) {
      return;
    }

    const dialogRef = this.dialog.open(RemoveServiceDialogComponent, {
      data: { serviceName: event.name },
      width: '540px',
      maxWidth: '90vw',
      panelClass: 'remove-service-dialog-panel',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          const updated = events.filter((e) => e.id !== event.id);
          this.storeService.setSelectedBookingEvents(updated);
        }
      });
  }

  onEnterDetails(): void {
    if (this.isRescheduleMode()) {
      this.router.navigate(['confirm-changes'], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve',
      });
    } else {
      // Ensure selected date and time slot are persisted before navigating
      const selectedDate = this.selectedDate();
      if (selectedDate) {
        this.storeService.setSelectedDate(selectedDate);
      }
      const idx = this.selectedSlotIndex();
      if (idx !== null) {
        const slots = this.displayTimeSlots();
        if (slots[idx]) {
          this.storeService.setSelectedTimeSlot(slots[idx].slot);
        }
      }
      this.storeService.setBookingStep('confirm');
    }
  }

  onBack(): void {
    if (this.isRescheduleMode()) {
      return;
    }
    const providerNeeded = this.storeService.getShouldShowProviderStep() && !this.storeService.getProviderStepSkipped();
    const backStep = providerNeeded ? 'provider' : 'scheduling';
    this.storeService.setBookingStep(backStep);
  }

  // --- Data loading ---

  loadTimeSlots(): void {
    this.cancelPendingLoad$.next();
    this.loadError.set(false);

    const month = this.currentMonth();
    const start = new Date(month.getFullYear(), month.getMonth(), 1);
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 1);

    this.isLoading.set(true);

    if (!this.isRescheduleMode()) {
      const selectedEvents = this.storeService.getSelectedBookingEvents();
      const selectedHost = this.storeService.getSelectedHost();
      const eventHostMap = this.storeService.getEventHostMap();

      this.targets = selectedEvents.map((event, index) => {
        // Only CLIENT_CHOICE requires a specific host userId; other types (ROUND_ROBIN, PRIORITY_ROUND_ROBIN, MULTI_HOST) are handled server-side
        const isClientChoice = event.eventType === TeamEventMeetingType.CLIENT_CHOICE;
        const hostId = eventHostMap.get(event.id) || selectedHost || undefined;
        return {
          eventTypeId: event.id,
          userId: isClientChoice ? hostId : undefined,
          order: index,
        };
      });
    }

    if (this.targets.length === 0) {
      this.isLoading.set(false);
      this.initialLoadComplete.set(true);
      return;
    }
    this.guestService
      .listBatchAvailableTimeSlots({
        targets: this.targets,
        timeSpan: trimTimeRangeToFromNow({ start, end }),
        timeZone: {
          id:
            this.timezone() || Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
      .pipe(
        takeUntil(this.cancelPendingLoad$),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.loadError.set(true);
          this.isLoading.set(false);
          this.initialLoadComplete.set(true);
          return of([]);
        })
      )
      .subscribe((timeSlots) => {
        const slots: TimeSlot[] = timeSlots || [];
        this.allTimeSlots.set(slots);
        this.slotsLoaded.set(true);
        this.isLoading.set(false);
        this.initialLoadComplete.set(true);

        if (this.pendingTimeSlotRestore) {
          const savedStart = new Date(
            this.pendingTimeSlotRestore.start
          ).getTime();
          // Match against displayTimeSlots (filtered by selected date), not all slots
          const displaySlots = this.displayTimeSlots();
          const matchIndex = displaySlots.findIndex(
            (item) => new Date(item.slot.start).getTime() === savedStart
          );
          if (matchIndex >= 0) {
            this.selectedSlotIndex.set(matchIndex);
          }
          this.pendingTimeSlotRestore = null;
        }
      });
  }
}
